import {
  type AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  Renderer2,
} from '@angular/core';

export interface CdwDropdownMenuOption {
  label: string;
  icon?: string;
  useMatIcon?: boolean;
  outlined?: boolean;
  fill?: boolean;
  iconColor?: string;
  iconSize?: string;
  value?: string;
  disabled?: boolean;
  separator?: boolean;
  tooltip?: string;
}

type ButtonLevel = 'primary' | 'secondary' | 'warn';

type ButtonMode = 'wide';

@Component({
  selector: 'lib-cdw-dropdown-menu',
  templateUrl: './cdw-dropdown-menu.component.html',
  styleUrls: ['./cdw-dropdown-menu.component.scss'],
  standalone: false,
})
export class CdwDropdownMenuComponent implements AfterViewInit {
  @Input() buttonLabel = 'Select';

  @Input() options: CdwDropdownMenuOption[] = [];

  @Input() buttonColor?: string;

  @Input() disabled = false;

  @Input() buttonSize?: string;

  @Input() level?: ButtonLevel;

  @Input() mode?: ButtonMode;

  @Input() customIcon?: string;

  @Input() customIconSize?: string;

  @Input() customIconPosition: 'left' | 'right' = 'left';

  @Input() dropdownArrowIcon = 'arrow_drop_down';

  @Input() dropdownArrowIconSize?: string;

  @Input() dropdownArrowPosition: 'left' | 'right' = 'right';

  @Input() useMatIcon = true;

  @Input() fontFamily?: string;

  @Input() fontSize?: string;

  @Input() fontWeight?: string | number;

  @Input() fontColor?: string;

  @Input() buttonWidth?: string;

  @Input() iconColor?: string;

  @Input() borderColor?: string;

  @Input() paddingLeft?: string;

  @Input() paddingRight?: string;

  @Input() panelWidth?: string;

  @Input() panelMaxHeight?: string;

  @Input() panelPosition: 'after' | 'before' = 'after';

  @Input() infoText?: string;

  @Input() infoBackgroundColor?: string;

  @Input() infoFontFamily?: string;

  @Input() infoFontSize?: string;

  @Input() infoFontWeight?: string | number;

  @Input() infoFontColor?: string;

  @Input() disabledTooltip?: string;

  @Output() optionSelected = new EventEmitter<CdwDropdownMenuOption>();

  private panelStyleId =
    `cdw-dropdown-panel-${Math.random().toString(36).substring(2, 11)}`;

  constructor(private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    if (this.panelWidth) {
      const style = this.renderer.createElement('style');
      const styleContent = this.renderer.createText(`
        .${this.panelStyleId}.mat-mdc-menu-panel {
          min-width: ${this.panelWidth} !important;
          max-width: ${this.panelWidth} !important;
          width: ${this.panelWidth} !important;
        }
      `);
      this.renderer.appendChild(style, styleContent);
      this.renderer.appendChild(document.head, style);
    }
  }

  public onOptionClick(option: CdwDropdownMenuOption): void {
    if (!option.disabled) {
      this.optionSelected.emit(option);
    }
  }

  public get panelClass(): string {
    return this.panelWidth ? this.panelStyleId : '';
  }

  public get buttonStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    if (this.fontFamily) {
      styles['font-family'] = this.fontFamily;
    }
    if (this.fontWeight) {
      styles['font-weight'] = String(this.fontWeight);
    }
    if (this.fontSize) {
      styles['font-size'] = this.fontSize;
    }
    if (this.fontColor) {
      styles['color'] = this.fontColor;
    }
    if (this.buttonColor) {
      styles['background-color'] = this.buttonColor;
      if (!this.fontColor) {
        styles['color'] = '#ffffff';
      }
    }
    if (this.buttonWidth) {
      styles['width'] = this.buttonWidth;
    }
    if (this.buttonSize) {
      styles['width'] = this.buttonSize;
    }
    if (this.borderColor) {
      styles['border-color'] = this.borderColor;
    }
    if (this.paddingLeft) {
      styles['padding-left'] = this.paddingLeft;
    }
    if (this.paddingRight) {
      styles['padding-right'] = this.paddingRight;
    }
    return styles;
  }

  public get buttonClasses(): string[] {
    const classes: string[] = [];
    if (this.customIconPosition === 'left') {
      classes.push('icon-left');
    } else if (this.customIconPosition === 'right') {
      classes.push('icon-right');
    }
    if (this.dropdownArrowPosition === 'left') {
      classes.push('arrow-left');
    } else if (this.dropdownArrowPosition === 'right') {
      classes.push('arrow-right');
    }
    if (this.level) {
      classes.push(`level-${this.level}`);
    }
    if (this.mode) {
      classes.push(`mode-${this.mode}`);
    }
    return classes;
  }
}
